/**
 * Cost Calculation and Compliance Tracking System
 * 
 * This module provides comprehensive cost calculation utilities for healthcare
 * lunch meeting compliance, including per-person expense tracking, spending
 * limits, and regulatory compliance monitoring.
 */

import { StaffStorage } from './staffStorage';
import { MeetingStorage } from './meetingStorage';
import { UserStorage } from './userStorage';

export const CostCalculation = {
  // Compliance limits and settings
  COMPLIANCE_LIMITS: {
    // Federal compliance limits per person per meal
    FEDERAL_MAX_PER_PERSON: 25.00,
    SUNSHINE_ACT_REPORTING_THRESHOLD: 10.00,
    
    // State-specific limits (can be extended)
    STATE_LIMITS: {
      'CA': 20.00,  // California
      'NY': 25.00,  // New York
      'TX': 25.00,  // Texas
      'FL': 25.00,  // Florida
      // Add more states as needed
    },
    
    // Company policy limits
    COMPANY_POLICY_MAX: 30.00,
    QUARTERLY_LIMIT_PER_PHYSICIAN: 500.00,
    ANNUAL_LIMIT_PER_PHYSICIAN: 2000.00
  },

  /**
   * Calculate total cost for a meeting including all attendees
   * @param {Object} meetingData - Meeting data with order information
   * @param {Array} staffOrders - Array of staff order objects
   * @returns {Object} Cost breakdown
   */
  calculateMeetingCost(meetingData, staffOrders = []) {
    const physicianCost = this._calculatePhysicianCost(meetingData.orderItems || []);
    const staffCosts = this._calculateStaffCosts(staffOrders);
    const totalCost = physicianCost + staffCosts.total;
    const totalAttendees = 1 + (staffOrders?.length || 0);
    const averageCostPerPerson = totalCost / totalAttendees;

    return {
      physicianCost,
      staffCosts: staffCosts.breakdown,
      totalStaffCost: staffCosts.total,
      totalCost,
      totalAttendees,
      averageCostPerPerson,
      complianceStatus: this._checkCompliance(totalCost, totalAttendees, meetingData.customerLocation)
    };
  },

  /**
   * Calculate physician's meal cost
   * @param {Array} orderItems - Physician's order items
   * @returns {number} Total cost
   */
  _calculatePhysicianCost(orderItems) {
    return orderItems.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + (price * quantity);
    }, 0);
  },

  /**
   * Calculate staff costs breakdown
   * @param {Array} staffOrders - Staff order data
   * @returns {Object} Staff cost breakdown
   */
  _calculateStaffCosts(staffOrders) {
    const breakdown = {};
    let total = 0;

    staffOrders.forEach(staffOrder => {
      const staffCost = this._calculateIndividualStaffCost(staffOrder.items || []);
      breakdown[staffOrder.staffId] = {
        name: staffOrder.staffName,
        role: staffOrder.role,
        cost: staffCost,
        items: staffOrder.items
      };
      total += staffCost;
    });

    return { breakdown, total };
  },

  /**
   * Calculate individual staff member's meal cost
   * @param {Array} items - Staff member's order items
   * @returns {number} Total cost for this staff member
   */
  _calculateIndividualStaffCost(items) {
    return items.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + (price * quantity);
    }, 0);
  },

  /**
   * Check compliance against various regulations
   * @param {number} totalCost - Total meeting cost
   * @param {number} attendeeCount - Number of attendees
   * @param {string} location - Meeting location (state)
   * @returns {Object} Compliance status
   */
  _checkCompliance(totalCost, attendeeCount, location = '') {
    const costPerPerson = totalCost / attendeeCount;
    const state = this._extractStateFromLocation(location);
    
    const compliance = {
      isCompliant: true,
      violations: [],
      warnings: [],
      costPerPerson,
      applicableLimits: {}
    };

    // Federal limit check
    if (costPerPerson > this.COMPLIANCE_LIMITS.FEDERAL_MAX_PER_PERSON) {
      compliance.isCompliant = false;
      compliance.violations.push({
        type: 'FEDERAL_LIMIT_EXCEEDED',
        message: `Cost per person ($${costPerPerson.toFixed(2)}) exceeds federal limit ($${this.COMPLIANCE_LIMITS.FEDERAL_MAX_PER_PERSON.toFixed(2)})`,
        limit: this.COMPLIANCE_LIMITS.FEDERAL_MAX_PER_PERSON,
        actual: costPerPerson
      });
    }
    compliance.applicableLimits.federal = this.COMPLIANCE_LIMITS.FEDERAL_MAX_PER_PERSON;

    // State limit check
    if (state && this.COMPLIANCE_LIMITS.STATE_LIMITS[state]) {
      const stateLimit = this.COMPLIANCE_LIMITS.STATE_LIMITS[state];
      compliance.applicableLimits.state = stateLimit;
      
      if (costPerPerson > stateLimit) {
        compliance.isCompliant = false;
        compliance.violations.push({
          type: 'STATE_LIMIT_EXCEEDED',
          message: `Cost per person ($${costPerPerson.toFixed(2)}) exceeds ${state} state limit ($${stateLimit.toFixed(2)})`,
          limit: stateLimit,
          actual: costPerPerson
        });
      }
    }

    // Company policy check
    if (costPerPerson > this.COMPLIANCE_LIMITS.COMPANY_POLICY_MAX) {
      compliance.warnings.push({
        type: 'COMPANY_POLICY_WARNING',
        message: `Cost per person ($${costPerPerson.toFixed(2)}) exceeds company policy recommendation ($${this.COMPLIANCE_LIMITS.COMPANY_POLICY_MAX.toFixed(2)})`,
        limit: this.COMPLIANCE_LIMITS.COMPANY_POLICY_MAX,
        actual: costPerPerson
      });
    }
    compliance.applicableLimits.companyPolicy = this.COMPLIANCE_LIMITS.COMPANY_POLICY_MAX;

    // Sunshine Act reporting threshold
    if (costPerPerson >= this.COMPLIANCE_LIMITS.SUNSHINE_ACT_REPORTING_THRESHOLD) {
      compliance.warnings.push({
        type: 'SUNSHINE_ACT_REPORTING_REQUIRED',
        message: `Cost per person ($${costPerPerson.toFixed(2)}) meets Sunshine Act reporting threshold ($${this.COMPLIANCE_LIMITS.SUNSHINE_ACT_REPORTING_THRESHOLD.toFixed(2)})`,
        threshold: this.COMPLIANCE_LIMITS.SUNSHINE_ACT_REPORTING_THRESHOLD,
        actual: costPerPerson
      });
    }

    return compliance;
  },

  /**
   * Extract state code from location string
   * @param {string} location - Location string
   * @returns {string|null} State code
   */
  _extractStateFromLocation(location) {
    if (!location) return null;
    
    // Try to extract state code (2 letters) from location
    const stateMatch = location.match(/\b([A-Z]{2})\b/);
    return stateMatch ? stateMatch[1] : null;
  },

  /**
   * Calculate quarterly spending for a physician
   * @param {string} physicianId - Physician ID
   * @param {number} year - Year
   * @param {number} quarter - Quarter (1-4)
   * @returns {Object} Quarterly spending summary
   */
  calculateQuarterlySpending(physicianId, year = new Date().getFullYear(), quarter = Math.ceil((new Date().getMonth() + 1) / 3)) {
    const meetings = MeetingStorage.getMeetingsForCustomer(physicianId);
    const quarterlyMeetings = this._filterMeetingsByQuarter(meetings, year, quarter);
    
    let totalSpending = 0;
    let meetingCount = 0;
    const meetingDetails = [];

    quarterlyMeetings.forEach(meeting => {
      if (meeting.status === 'completed' && meeting.totalCost) {
        const cost = parseFloat(meeting.totalCost) || 0;
        totalSpending += cost;
        meetingCount++;
        meetingDetails.push({
          id: meeting.id,
          date: meeting.date,
          cost: cost,
          attendeeCount: meeting.totalAttendees || 1,
          salesRep: meeting.salesRepName
        });
      }
    });

    const remainingQuarterlyBudget = this.COMPLIANCE_LIMITS.QUARTERLY_LIMIT_PER_PHYSICIAN - totalSpending;
    const isOverBudget = totalSpending > this.COMPLIANCE_LIMITS.QUARTERLY_LIMIT_PER_PHYSICIAN;

    return {
      physicianId,
      year,
      quarter,
      totalSpending,
      meetingCount,
      averagePerMeeting: meetingCount > 0 ? totalSpending / meetingCount : 0,
      quarterlyLimit: this.COMPLIANCE_LIMITS.QUARTERLY_LIMIT_PER_PHYSICIAN,
      remainingBudget: remainingQuarterlyBudget,
      isOverBudget,
      utilizationPercentage: (totalSpending / this.COMPLIANCE_LIMITS.QUARTERLY_LIMIT_PER_PHYSICIAN) * 100,
      meetingDetails
    };
  },

  /**
   * Calculate annual spending for a physician
   * @param {string} physicianId - Physician ID
   * @param {number} year - Year
   * @returns {Object} Annual spending summary
   */
  calculateAnnualSpending(physicianId, year = new Date().getFullYear()) {
    const quarterlyData = [];
    let totalAnnualSpending = 0;
    let totalMeetings = 0;

    // Calculate for each quarter
    for (let quarter = 1; quarter <= 4; quarter++) {
      const quarterData = this.calculateQuarterlySpending(physicianId, year, quarter);
      quarterlyData.push(quarterData);
      totalAnnualSpending += quarterData.totalSpending;
      totalMeetings += quarterData.meetingCount;
    }

    const remainingAnnualBudget = this.COMPLIANCE_LIMITS.ANNUAL_LIMIT_PER_PHYSICIAN - totalAnnualSpending;
    const isOverBudget = totalAnnualSpending > this.COMPLIANCE_LIMITS.ANNUAL_LIMIT_PER_PHYSICIAN;

    return {
      physicianId,
      year,
      totalAnnualSpending,
      totalMeetings,
      averagePerMeeting: totalMeetings > 0 ? totalAnnualSpending / totalMeetings : 0,
      annualLimit: this.COMPLIANCE_LIMITS.ANNUAL_LIMIT_PER_PHYSICIAN,
      remainingBudget: remainingAnnualBudget,
      isOverBudget,
      utilizationPercentage: (totalAnnualSpending / this.COMPLIANCE_LIMITS.ANNUAL_LIMIT_PER_PHYSICIAN) * 100,
      quarterlyBreakdown: quarterlyData
    };
  },

  /**
   * Filter meetings by quarter
   * @param {Array} meetings - Array of meetings
   * @param {number} year - Year
   * @param {number} quarter - Quarter (1-4)
   * @returns {Array} Filtered meetings
   */
  _filterMeetingsByQuarter(meetings, year, quarter) {
    const quarterStartMonth = (quarter - 1) * 3;
    const quarterEndMonth = quarterStartMonth + 2;

    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.date);
      const meetingYear = meetingDate.getFullYear();
      const meetingMonth = meetingDate.getMonth();

      return meetingYear === year && 
             meetingMonth >= quarterStartMonth && 
             meetingMonth <= quarterEndMonth;
    });
  },

  /**
   * Generate compliance report for a meeting
   * @param {string} meetingId - Meeting ID
   * @returns {Object} Detailed compliance report
   */
  generateMeetingComplianceReport(meetingId) {
    const meeting = MeetingStorage.getMeetingById(meetingId);
    if (!meeting) {
      throw new Error('Meeting not found');
    }

    const staffOrders = StaffStorage.getStaffOrdersForMeeting(meetingId);
    const costBreakdown = this.calculateMeetingCost(meeting, staffOrders);
    
    const physicianSpending = this.calculateQuarterlySpending(
      meeting.customerId, 
      new Date(meeting.date).getFullYear(),
      Math.ceil((new Date(meeting.date).getMonth() + 1) / 3)
    );

    return {
      meetingId,
      meetingDate: meeting.date,
      physician: {
        id: meeting.customerId,
        name: meeting.customer,
        location: meeting.customerLocation || ''
      },
      salesRep: {
        id: meeting.salesRepId,
        name: meeting.salesRepName
      },
      costBreakdown,
      complianceStatus: costBreakdown.complianceStatus,
      physicianSpendingContext: {
        quarterlyTotal: physicianSpending.totalSpending,
        quarterlyLimit: physicianSpending.quarterlyLimit,
        remainingQuarterlyBudget: physicianSpending.remainingBudget
      },
      reportGenerated: new Date().toISOString()
    };
  },

  /**
   * Export compliance data to CSV format
   * @param {Array} meetings - Array of meetings
   * @returns {string} CSV content
   */
  exportComplianceDataToCSV(meetings) {
    const headers = [
      'Meeting Date',
      'Meeting ID', 
      'Physician Name',
      'Sales Rep',
      'Total Attendees',
      'Total Cost',
      'Cost Per Person',
      'Compliance Status',
      'Violations',
      'Restaurant',
      'Location'
    ];

    const rows = meetings.map(meeting => {
      const staffOrders = StaffStorage.getStaffOrdersForMeeting(meeting.id);
      const costData = this.calculateMeetingCost(meeting, staffOrders);
      
      return [
        meeting.date,
        meeting.id,
        meeting.customer,
        meeting.salesRepName,
        costData.totalAttendees,
        `$${costData.totalCost.toFixed(2)}`,
        `$${costData.averageCostPerPerson.toFixed(2)}`,
        costData.complianceStatus.isCompliant ? 'Compliant' : 'Non-Compliant',
        costData.complianceStatus.violations.map(v => v.type).join('; '),
        meeting.restaurant || '',
        meeting.customerLocation || ''
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }
};

// Utility functions for cost formatting and validation
export const CostUtils = {
  /**
   * Format currency for display
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  },

  /**
   * Validate meeting cost data
   * @param {Object} costData - Cost data to validate
   * @returns {Object} Validation result
   */
  validateCostData(costData) {
    const errors = [];
    
    if (!costData.totalCost || costData.totalCost < 0) {
      errors.push('Invalid total cost');
    }
    
    if (!costData.totalAttendees || costData.totalAttendees < 1) {
      errors.push('Invalid attendee count');
    }
    
    if (costData.averageCostPerPerson > 100) {
      errors.push('Cost per person seems unusually high');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Calculate percentage of budget used
   * @param {number} spent - Amount spent
   * @param {number} budget - Total budget
   * @returns {number} Percentage used
   */
  calculateBudgetUtilization(spent, budget) {
    if (budget <= 0) return 0;
    return Math.min((spent / budget) * 100, 100);
  }
};